import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Plus, Trash, Eye, X, CheckCircle, WarningCircle, Info, Sparkle, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_META = {
  info: { label: 'Info', icon: Info, color: 'text-blue-400' },
  success: { label: 'Success', icon: CheckCircle, color: 'text-green-400' },
  warning: { label: 'Warning', icon: WarningCircle, color: 'text-amber-400' },
  announcement: { label: 'Announcement', icon: Sparkle, color: 'text-blue-400' },
};

const AUDIENCES = [
  { key: 'all', label: 'Everyone' },
  { key: 'viewers', label: 'Viewers only' },
  { key: 'creators', label: 'Creators only' },
  { key: 'admins', label: 'Admins only' },
];

const BroadcastsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '',
    body: '',
    audience: 'all',
    severity: 'announcement',
    cta_label: '',
    cta_url: '',
    also_email: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/broadcasts/admin/list`);
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/broadcasts`, form);
      toast.success('Broadcast published — active for all matching users');
      setOpen(false);
      setForm({ title: '', body: '', audience: 'all', severity: 'announcement', cta_label: '', cta_url: '', also_email: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try {
      await axios.patch(`${API}/api/broadcasts/${id}/toggle`);
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this broadcast? Dismissals are also removed.')) return;
    try {
      await axios.delete(`${API}/api/broadcasts/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="broadcasts-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Megaphone weight="fill" className="w-5 h-5 text-blue-400" />
            Mass Messages
          </h2>
          <p className="text-sm text-slate-500">Published messages appear as a dismissible banner on every page for matching users until they dismiss it.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center gap-2"
              data-testid="new-broadcast-btn">
              <Plus className="w-5 h-5" /> New Message
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Broadcast Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Scheduled maintenance tonight at 11 PM"
                  maxLength={120}
                  className="bg-slate-800 border-white/10" data-testid="broadcast-title" />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                  placeholder="Tell them what's up…"
                  maxLength={2000} rows={4}
                  className="bg-slate-800 border-white/10" data-testid="broadcast-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Audience</Label>
                  <select value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-white"
                    data-testid="broadcast-audience">
                    {AUDIENCES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Tone</Label>
                  <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-white"
                    data-testid="broadcast-severity">
                    {Object.entries(SEVERITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>CTA label (optional)</Label>
                  <Input value={form.cta_label} onChange={e => setForm({ ...form, cta_label: e.target.value })}
                    placeholder="Read more" className="bg-slate-800 border-white/10" />
                </div>
                <div>
                  <Label>CTA link (optional)</Label>
                  <Input value={form.cta_url} onChange={e => setForm({ ...form, cta_url: e.target.value })}
                    placeholder="/explore" className="bg-slate-800 border-white/10" />
                </div>
              </div>

              {/* Email blast toggle */}
              <label className="flex items-start gap-3 p-3 bg-slate-800/60 hover:bg-slate-800 rounded-xl cursor-pointer border border-white/5 transition-colors"
                data-testid="broadcast-also-email-label">
                <input
                  type="checkbox"
                  checked={form.also_email}
                  onChange={e => setForm({ ...form, also_email: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-blue-500"
                  data-testid="broadcast-also-email-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                    <EnvelopeSimple weight="fill" className="w-4 h-4 text-blue-400" />
                    Also send as email
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Sends to every user in the selected audience who has <span className="text-slate-400">Announcements &amp; news</span> emails enabled in their settings. Default is on, so most users will receive it.
                  </p>
                </div>
              </label>

              <button onClick={create} disabled={saving}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
                data-testid="publish-broadcast-btn">
                {saving ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No broadcasts yet. Send your first message to the user base.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(b => {
            const meta = SEVERITY_META[b.severity] || SEVERITY_META.info;
            const Icon = meta.icon;
            return (
              <div key={b.id} className="glass-light rounded-xl p-4" data-testid={`broadcast-${b.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon weight="fill" className={`w-4 h-4 ${meta.color}`} />
                      <span className="font-semibold text-white">{b.title}</span>
                      {!b.is_active && (
                        <span className="px-2 py-0.5 bg-slate-800 text-slate-500 text-xs rounded-full">Paused</span>
                      )}
                      <span className="px-2 py-0.5 bg-slate-800 text-slate-400 text-xs rounded-full">
                        {AUDIENCES.find(a => a.key === b.audience)?.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 line-clamp-2">{b.body}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {b.seen_count} seen
                      </span>
                      <span>· {b.dismissed_count} dismissed</span>
                      {b.emailed && b.email_stats && (
                        <span className="flex items-center gap-1 text-blue-400/80">
                          · <EnvelopeSimple weight="fill" className="w-3 h-3" />
                          {b.email_stats.sent}/{b.email_stats.total} emailed
                          {b.email_stats.failed > 0 && <span className="text-amber-400"> ({b.email_stats.failed} failed)</span>}
                        </span>
                      )}
                      <span>· by {b.created_by}</span>
                      <span>· {new Date(b.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggle(b.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        b.is_active
                          ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}
                      data-testid={`toggle-broadcast-${b.id}`}>
                      {b.is_active ? 'Pause' : 'Resume'}
                    </button>
                    <button onClick={() => remove(b.id)}
                      className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg"
                      data-testid={`delete-broadcast-${b.id}`}>
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BroadcastsTab;
